import { FC, useEffect, useState } from "react";
import SubscribeIconSVG from "../../resources/svg/SubscribeIconSVG";
import { useTranslation } from "react-i18next";
import CurrencyIconSVG from "../../resources/svg/CurrencyIconSVG";
import useScreenSize from "../../../custom-hooks/base-hooks/useScreenSize";
import ClockIconSVG from "../../resources/svg/ClockIconSVG";
import CustomCheckbox from "../../../custom-components/form-inputs/CustomCheckbox";
import CheckMarkIconSVG from "../../resources/svg/CheckMarkIconSVG";
import EasyPayLogoSVG from "../../resources/svg/logos/EasyPayLogoSVG";
import ArchiveBoxIconSVG from "../../resources/svg/ArchiveBoxIconSVG";
import SubscriptionsArchivePopup from "./SubscriptionsArchivePopup";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import {
    subscriptionCreationFields,
    subscriptionsCreationSchemaFactory,
} from "../../../validation-schemas/subscription-schemas/SubscriptionsCreationSchema";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import { SubscriptionServiceImpl } from "../../../services/SubscriptionServiceImpl";
import CustomRadioButton from "../../../custom-components/form-inputs/CustomRadioButton";
import { PaymentMethod } from "../../../domain/enums/PaymentMethodEnum";
import CustomError from "../../../custom-components/form-inputs/CustomError";
import { useSelector } from "react-redux";
import {
    selectActiveSubscription,
    selectIsTrialSubscriptionAvailable,
    selectPriceDollars,
    selectPriceHryvnia,
    selectSubscriptionDurationDays,
} from "../../../slices/subscriptions-slice";
import SubscriptionStatusLabel from "../../resources/components/subscription-components/SubscriptionStatusLabel";
import SubscriptionDateTimeBlock from "./SubscriptionDateTimeBlock";
import CustomToast from "../../../custom-components/CustomToast";
import AccomplishedIconSVG from "../../resources/svg/AccomplishedIconSVG";
import useToastMessage from "../../../custom-hooks/useToastMessage";
import { ResponseMessages } from "../../../domain/enums/common/ResponseMessagesEnum";
import SubscriptionSuccess from "./SubscriptionSuccess";
import _ from "lodash";
import { useSearchParams } from "react-router-dom";
import { disableRecurrentPayments } from "../../../Config";

const SUCCESS_KEY = "success";

const SubscriptionForm: FC = () => {
    const [isArchiveVisible, setArchiveVisible] = useState(false);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { message, setMessage, resetMessage } = useToastMessage();
    const { moreOrEqual800 } = useScreenSize();

    const subscription = useSelector(selectActiveSubscription);
    const priceDollars = useSelector(selectPriceDollars);
    const priceHryvnia = useSelector(selectPriceHryvnia);
    const durationDays = useSelector(selectSubscriptionDurationDays);
    const trialAvailable = useSelector(selectIsTrialSubscriptionAvailable);

    const [searchParams, setSearchParams] = useSearchParams();
    const successParam = searchParams.get(SUCCESS_KEY)?.toLowerCase();
    const isPaymentResultVisible = successParam === "true" || successParam === "false";
    const isSuccess = successParam === "true";
    const refreshSubscriptions = () => dispatch(SubscriptionServiceImpl.getAllSubscriptionsThunk());
    const closeSuccessFormCallback = () => {
        setSearchParams({});
        refreshSubscriptions();
    };

    const { form, setErrorKey, errorKey } = useCustomFormik({
        initial: subscription,
        fields: subscriptionCreationFields,
        schema: subscriptionsCreationSchemaFactory(t),
        onSubmit: async (values) => {
            if (!form.values.consent || !form.touched.consent) {
                setErrorKey("errors.consentRequired");
                return;
            }
            const subscription = await dispatch(SubscriptionServiceImpl.createSubscriptionThunk(values));
            if (subscription.meta.requestStatus !== "fulfilled") {
                setErrorKey("errors.common");
                return;
            }

            const forwardUrl = _.get(subscription, "payload.forwardUrl");
            if (forwardUrl) {
                window.open(forwardUrl, "_blank");
                return;
            }

            setSearchParams({ [SUCCESS_KEY]: "true" });
        },
    });

    useEffect(() => {
        refreshSubscriptions();
    }, [successParam]);

    useEffect(() => {
        const refreshOnFocus = () => refreshSubscriptions();
        const refreshOnVisibility = () => {
            if (document.visibilityState === "visible") refreshSubscriptions();
        };

        window.addEventListener("focus", refreshOnFocus);
        document.addEventListener("visibilitychange", refreshOnVisibility);
        return () => {
            window.removeEventListener("focus", refreshOnFocus);
            document.removeEventListener("visibilitychange", refreshOnVisibility);
        };
    }, []);

    useEffect(() => {
        dispatch(SubscriptionServiceImpl.getSubscriptionSettingsThunk(form.values.type));
    }, [form.values.type]);

    const autoActivationCheckboxHandler = async () => {
        form.setFieldValue("autoActivation", !form.values.autoActivation);
        if (!subscription) {
            return;
        }
        const result = await dispatch(SubscriptionServiceImpl.changeSubscriptionAutoActivationThunk());
        if (result.meta.requestStatus === "fulfilled") {
            setMessage(
                form.values.autoActivation ? "subscription.autoContinueEnabled" : "subscription.autoContinueDisabled",
                ResponseMessages.SUCCESS
            );
        }
    };

    const consentCheckboxHandler = () => {
        form.setFieldValue("consent", !form.values.consent);
    };

    return (
        <div
            id="subscription"
            className="relative flex flex-col bg-[rgba(255,255,255,0.05)] rounded-lg dmax2:rounded-md px-[38px] td4:px-5 tmax4:px-[15px] py-[38px] dmax2:py-6 w-full mb-[80px] dmax2:mb-[60px]"
        >
            {moreOrEqual800 && (
                <SubscribeIconSVG className="absolute top-6 right-6 w-[157px] h-[141px] text-[rgba(122,122,126,0.1)]" />
            )}
            <div className="flex items-center mb-8 dmax2:mb-6">
                <span className="H20 dmax2:H16B text-textWhite mr-5">{t("subscription.status.title")}</span>
                <SubscriptionStatusLabel subscription={subscription} />
            </div>
            <div className="flex flex-wrap gap-3 items-center mb-8 dmax2:mb-6">
                <div className="flex items-center bg-[rgba(255,255,255,0.05)] rounded-[9px] h-[64px] dmax2:h-12 px-6 dmax2:px-4 tmax4:w-full">
                    <CurrencyIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 text-textWhite mr-3" />
                    <span className="H20 dmax2:H14B text-textWhite mr-5 dmax2:mr-3">{t("subscription.cost")}</span>
                    <span className="H20 dmax2:H16B text-textWhite mr-3">
                        {priceDollars?.amount} {priceDollars && t(`currencies.short.${priceDollars.currency}`)}
                    </span>
                    <span className="H16 dmax2:H14 text-textFaded mr-3">{t("subscription.or")}</span>
                    <span className="H20 dmax2:H16B text-textWhite mr-3">
                        {priceHryvnia
                            ? `${priceHryvnia.amount} ${t(`currencies.short.${priceHryvnia.currency}`)}`
                            : t("currencies.conversionUnavailable")}
                    </span>
                </div>
                <span className="H16 dmax2:H14 text-textFaded tmax4:w-[254px]">{t("subscription.disclaimer")}</span>
            </div>
            <div className="flex items-center bg-[rgba(255,255,255,0.05)] rounded-[9px] h-[64px] dmax2:h-12 px-6 dmax2:px-4 w-fit tmax4:w-full mb-8 dmax2:mb-6">
                <ClockIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 text-textWhite mr-3" />
                <span className="H20 dmax2:H14B text-textWhite mr-5 dmax2:mr-3">{t("subscription.duration")}</span>
                <span className="H20 dmax2:H16B text-textWhite">{t("subscription.days", { days: durationDays })}</span>
            </div>
            <SubscriptionDateTimeBlock />
            <div className="relative flex tmax4:flex-col gap-8 tmax4:gap-4 items-center tmax4:items-start mb-8 dmax2:mb-6">
                {!disableRecurrentPayments && <CustomCheckbox
                    labelKey="subscription.autoContinue"
                    checked={form.values.autoActivation}
                    disabled={trialAvailable || subscription?.trial || subscription?.referral}
                    onClick={autoActivationCheckboxHandler}
                    labelClassName="H16 td4:H14B text-textFaded"
                    checkboxClassName="flex justify-center items-center w-6 h-6 border-2 border-textFaded rounded-sm cursor-pointer transition duration-200 mr-4 tmax4:mr-3"
                    checkboxCheckedClassName="!border-0 bg-textWarning"
                    wrapClassName="flex items-center"
                    additionalLayoutElement={<CheckMarkIconSVG />}
                />}
                <CustomToast
                    messageObj={message}
                    resetMessageObj={resetMessage}
                    wrapClassName="w-full"
                    staticClassName="w-full"
                    staticType
                    singleMessageMode
                    tooltipElement={
                        <div className="absolute top-4 ti7:top-6 tmax4:top-7 left-9 H10 text-textWhite bg-backgroundInput rounded-[30px] w-fit h-[25px] flex items-center px-3">
                            {t(
                                `subscription.${
                                    form.values.autoActivation ? "autoContinueEnabled" : "autoContinueDisabled"
                                }`
                            )}
                            <AccomplishedIconSVG className="w-4 h-4 text-textWhite ml-1.5" />
                        </div>
                    }
                />
                <CustomCheckbox
                    labelKey="subscription.consent"
                    checked={form.values.consent}
                    onClick={consentCheckboxHandler}
                    labelClassName="H16 td4:H14B text-textFaded"
                    checkboxClassName="flex justify-center items-center w-6 h-6 border-2 border-textFaded rounded-sm cursor-pointer transition duration-200 mr-4 tmax4:mr-3"
                    checkboxCheckedClassName="!border-0 bg-textWarning"
                    wrapClassName={`flex items-center ${subscription && "opacity-30 pointer-events-none"}`}
                    additionalLayoutElement={<CheckMarkIconSVG />}
                />
            </div>
            <CustomError className="mb-8 dmax2:mb-6" errorKey={errorKey} />
            <span
                className={`H24B dmax2:H20B text-textWhite mb-6 dmax2:mb-4 ${
                    subscription && "opacity-30 pointer-events-none"
                }`}
            >
                {t("subscription.paymentMethodTitle")}
            </span>
            <CustomRadioButton
                names={["paymentMethod"]}
                form={form}
                fields={subscriptionCreationFields}
                firstWrapClassName={`flex dmax2:flex-col justify-between w-full dmin2:w-[1192px] ${
                    subscription && "opacity-30 pointer-events-none"
                }`}
                secondWrapClassName="flex flex-col-reverse"
                thirdWrapClassName="flex items-center"
                checkedRadioOuterClassName="min-w-8 dmax2:min-w-6 h-8 dmax2:h-6 rounded-full bg-transparent border-[2px] border-textWarning transition duration-300 mr-3 dmax2:mr-2"
                checkedRadioInnerClassName="bg-textWarning w-[14px] h-[14px]"
                uncheckedRadioClassName="min-w-8 dmax2:min-w-6 h-8 dmax2:h-6 bg-transparent border border-textFaded transition duration-300 mr-3 dmax2:mr-2"
                extraLabelKeys={[t(`paymentMethods.${PaymentMethod.EASY_PAY}`)]}
                labelClassName="H20 text-textWhite"
                extraContentArray={[
                    <div
                        key="subscription-payment-method-1"
                        className={`flex flex-col ${subscription && "opacity-30 pointer-events-none"}`}
                    >
                        <div className="flex items-center justify-center rounded-[8px] w-[193px] dmax2:w-[142px] h-[105px] dmax2:h-[81px] bg-backgroundButton border-2 border-[rgba(255,255,255,0.05)] mb-3">
                            <EasyPayLogoSVG className="w-[113px] dmax2:w-[82px]" />
                        </div>
                    </div>,
                ]}
            />
            <div className="flex tmax4:flex-col tmax4:gap-5 tmin4:justify-between mt-8 dmax2:mt-6">
                <div
                    onClick={() => form.handleSubmit()}
                    className={`button H16 dmax2:H14B text-textWhite h-[50px] dmax2:h-[42px] w-fit px-6 dmax2:px-4 transition duration-300 ${
                        subscription && "opacity-30 pointer-events-none"
                    }`}
                >
                    {t("subscription.goToPayment")}
                </div>
                <div onClick={() => setArchiveVisible(true)} className="flex items-center cursor-pointer group">
                    <ArchiveBoxIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 text-textFaded group-hover:text-textWhite transition duration-300 mr-2" />
                    <span className="H14 text-textFaded group-hover:text-textWhite transition duration-300">
                        {t("subscription.subscribesArchive")}
                    </span>
                </div>
            </div>
            <SubscriptionsArchivePopup isVisible={isArchiveVisible} setIsVisible={setArchiveVisible} />
            <SubscriptionSuccess
                isVisible={isPaymentResultVisible}
                isSuccess={isSuccess}
                isActivationConfirmed={Boolean(subscription)}
                setVisible={closeSuccessFormCallback}
            />
        </div>
    );
};

export default SubscriptionForm;
